<template>
  <div v-if="hasContent" class="message-footer" :class="footerClasses" role="group" :aria-label="ariaLabel">
    <!-- Status icon: ✓ sent, ✓✓ delivered/read, ⏱ sending, or retry button; each has aria-label for screen readers -->
    <div v-if="showStatus" class="message-footer__status">
      <button
        v-if="status === 'failed'"
        type="button"
        class="message-footer__retry"
        title="تلاش مجدد"
        aria-label="تلاش مجدد برای ارسال پیام"
        @click="$emit('retry')"
        @keydown.enter.prevent="$emit('retry')"
        @keydown.space.prevent="$emit('retry')"
      >
        <v-icon color="#F44336" size="14" aria-hidden="true">mdi-refresh</v-icon>
      </button>
      <span
        v-else-if="status === 'read'"
        class="message-footer__icon-wrap"
        role="img"
        :aria-label="statusAriaLabel"
      >
        <v-icon size="14" class="message-footer__icon message-footer__icon--read" aria-hidden="true">mdi-check-all</v-icon>
      </span>
      <span
        v-else-if="status === 'delivered'"
        class="message-footer__icon-wrap"
        role="img"
        :aria-label="statusAriaLabel"
      >
        <v-icon size="14" class="message-footer__icon message-footer__icon--delivered" aria-hidden="true">mdi-check-all</v-icon>
      </span>
      <span
        v-else-if="status === 'sending'"
        class="message-footer__icon-wrap"
        role="img"
        :aria-label="statusAriaLabel"
      >
        <v-icon size="14" class="message-footer__icon message-footer__icon--sending message-footer__icon--pulse" aria-hidden="true">mdi-clock-outline</v-icon>
      </span>
      <span
        v-else
        class="message-footer__icon-wrap"
        role="img"
        :aria-label="statusAriaLabel"
      >
        <v-icon size="14" class="message-footer__icon message-footer__icon--sent" aria-hidden="true">mdi-check</v-icon>
      </span>
    </div>
    <!-- Timestamp: only when present; title for hover tooltip with full date -->
    <span
      v-if="showTime"
      class="message-footer__time"
      :title="timestampTitle || formattedTime"
    >{{ formattedTime }}</span>
  </div>
</template>

<script>
export default {
  name: 'MessageFooter',
  props: {
    /** Formatted time string (e.g. "09:44"). Omit or empty to hide. */
    formattedTime: {
      type: String,
      default: ''
    },
    /** Delivery status: 'sending' | 'sent' | 'delivered' | 'read' | 'failed' */
    status: {
      type: String,
      default: 'sent'
    },
    /** Whether to show the footer row (e.g. last message in group). */
    showTimestamp: {
      type: Boolean,
      default: true
    },
    /** User (blue) bubble styling; false = support (gray) bubble. */
    isUser: {
      type: Boolean,
      default: false
    },
    /** Full timestamp tooltip (e.g. "Sent: 09:44 on 2026-02-16"). Shown on hover. */
    timestampTitle: {
      type: String,
      default: ''
    }
  },
  computed: {
    statusAriaLabel() {
      const labels = {
        sent: 'ارسال شده',
        delivered: 'تحویل داده شده',
        read: 'خوانده شده',
        sending: 'در حال ارسال'
      };
      return labels[this.status] || labels.sent;
    },
    showStatus() {
      return this.isUser;
    },
    showTime() {
      return this.showTimestamp && this.formattedTime != null && String(this.formattedTime).trim() !== '';
    },
    hasContent() {
      return this.showStatus || this.showTime;
    },
    footerClasses() {
      return {
        'message-footer--user': this.isUser,
        'message-footer--support': !this.isUser
      };
    },
    ariaLabel() {
      if (!this.showStatus && !this.showTime) return '';
      const parts = [];
      if (this.showStatus && this.status === 'failed') parts.push('ارسال ناموفق');
      if (this.showStatus && this.status !== 'failed') parts.push(this.statusAriaLabel);
      if (this.showTime) parts.push(`زمان: ${this.formattedTime}`);
      return parts.length ? parts.join('، ') : '';
    }
  }
};
</script>

<style scoped>
/* Reusable footer: [status icon] [timestamp], 8px gap, right-aligned in RTL */
.message-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
  padding-top: 0;
  padding-bottom: 0;
  direction: rtl;
  min-height: 0;
}

.message-footer--support {
  justify-content: flex-end;
}

.message-footer--user {
  justify-content: flex-end;
}

[dir="ltr"] .message-footer {
  justify-content: flex-end;
}

.message-footer__status {
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 1;
}

.message-footer__icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Checkmark/icons: high contrast on blue for WCAG (aim for best possible; 3:1 for UI components) */
.message-footer__icon {
  color: #E8F4FF !important;
  opacity: 1;
}

.message-footer__icon--sending {
  color: #B8D4F0 !important;
}

@keyframes messageFooterPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.message-footer__icon--pulse {
  animation: messageFooterPulse 1.5s ease-in-out infinite;
}

/* Timestamp: high contrast on blue; WCAG AA aim (3:1+ for UI/large text) */
.message-footer__time {
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  margin: 0;
  color: #FFFFFF;
  white-space: nowrap;
}

/* Support (gray) bubble: timestamp only, no status icon color override */
.message-footer--support .message-footer__time {
  color: #787878;
}

.message-footer__retry {
  background: rgba(244, 67, 54, 0.1);
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  min-width: 28px;
  min-height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background 0.2s ease, transform 0.2s ease;
}

.message-footer__retry:hover {
  background: rgba(244, 67, 54, 0.2);
}

.message-footer__retry:focus {
  outline: none;
}

.message-footer__retry:focus-visible {
  outline: 2px solid #5B7CFA;
  outline-offset: 2px;
}
</style>
